'use client';

/**
 * API namespace `disputes` (sub-namespace within parent).
 * Verbatim extraction from frontend/src/lib/api-client.ts (L7962-L8188).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-4, T-EXPORT, T-IMPORTS (see ledger).
 */

import { apiFetch, apiFetchFormData } from '../core/fetch-client';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

export const disputes = {
  /**
   * POST /api/market/disputes — buyer opens a dispute on an order.
   *
   * Pre-conditions (enforced in `dispute.service.openDispute` — Task B):
   *   - `order.status === 'delivered' || 'completed'` (no pending/
   *     cancelled).
   *   - `order.buyerId === req.user.sub` (ownership).
   *   - NO existing open dispute for this orderNumber (1 dispute per
   *     order — INV-MKT-16).
   *   - 2FA required (operation may result in refund — INV-SEC-06).
   *
   * Backend flow: `dispute.service.openDispute` creates the Dispute
   * document with `status='open'`, `evidenceWindowExpiresAt = now + 48h`
   * (INV-MKT-16), `slaExpiresAt = now + 7d` (INV-MKT-16). Q4 auto-
   * mediation check (suggestive only — sets `mediationSuggestion.
   * suggested=true` if seller.rating > 4.8 AND totalOrders >= 50 AND
   * buyer.market.totalPurchases < 5). Returns the new Dispute with
   * empty messages + evidence arrays.
   *
   * Authz: `requireAuth` + `requireMarketDisputesEnabled` +
   * `orderDisputeLimiter` + `requireRecent2FA` (TOTP/wallet 2FA
   * within last 5 min — financial-grade). On 403 `2FA_REQUIRED`, the
   * caller's catch block re-opens the `TwoFactorFinancialDialog`.
   *
   * Spec ref: SPEC-084 Fase 6 §5.1 (POST /disputes), §4.1 step 1
   *           (openDispute flow); SPEC-NEGOCIO §10.9 INV-MKT-16.
   */
  async open(
    orderNumber: string,
    body: { channel: DisputeChannel; reason: string },
    signal?: AbortSignal,
  ): Promise<DisputeResponse> {
    return apiFetch<DisputeResponse>('/market/disputes', {
      method: 'POST',
      body: JSON.stringify({ orderNumber, channel: body.channel, reason: body.reason }),
      signal,
    });
  },
  /**
   * GET /api/market/disputes — list the caller's disputes (paginated).
   * Returns `DisputeListResponse` (`disputes` summary array + `total`).
   * The optional `status` filter accepts any `DisputeStatus` value.
   *
   * Authz: self only — backend verifies `dispute.buyerId === req.user!
   * sub` OR `dispute.sellerId === req.user.sub` per dispute.
   *
   * Spec ref: SPEC-084 Fase 6 §5.1 (GET /disputes — listMine); §4.1
   *           step 1 (listBuyerOrSellerDisputes).
   */
  async listMine(
    status?: DisputeStatus,
    signal?: AbortSignal,
  ): Promise<DisputeListResponse> {
    const qs = new URLSearchParams();
    if (status) qs.set('status', status);
    const query = qs.toString();
    return apiFetch<DisputeListResponse>(
      query ? `/market/disputes?${query}` : '/market/disputes',
      { signal },
    );
  },
  /**
   * GET /api/market/disputes/:disputeId — dispute detail (with messages
   * + evidence). Returns `DisputeResponse`.
   *
   * Authz: ownership — backend verifies `dispute.buyerId === req.user!
   * sub` OR `dispute.sellerId === req.user.sub` OR `req.user.role ∈
   * {admin, superadmin}`.
   *
   * Spec ref: SPEC-084 Fase 6 §5.1 (GET /:disputeId); §4.1 step 1
   *           (getDisputeDetail).
   */
  async get(
    disputeId: string,
    signal?: AbortSignal,
  ): Promise<DisputeResponse> {
    return apiFetch<DisputeResponse>(
      `/market/disputes/${encodeURIComponent(disputeId)}`,
      { signal },
    );
  },
  /**
   * POST /api/market/disputes/:disputeId/messages — post a chat message
   * to the dispute thread. Append-only (INV-MKT-16 — chat is permanent
   * evidence). Returns `DisputeMessageResponse` with the new `messageId`
   * + `at` timestamp so the caller can optimistically append.
   *
   * Authz: ownership (buyerId or sellerId === req.user.sub, OR admin).
   * NO 2FA gate (the chat does not move credits — admin resolution does).
   *
   * Spec ref: SPEC-084 Fase 6 §5.1 (POST /:disputeId/messages); §4.1
   *           step 2 (postMessage flow); SPEC-NEGOCIO §10.9 (INV-MKT-16
   *           chat permanente como evidencia).
   */
  async postMessage(
    disputeId: string,
    body: { text: string },
    signal?: AbortSignal,
  ): Promise<DisputeMessageResponse> {
    return apiFetch<DisputeMessageResponse>(
      `/market/disputes/${encodeURIComponent(disputeId)}/messages`,
      {
        method: 'POST',
        body: JSON.stringify({ text: body.text }),
        signal,
      },
    );
  },
  /**
   * POST /api/market/disputes/:disputeId/evidence — upload evidence file
   * (multipart/form-data). Append-only sub-doc on `Dispute.evidence[]`.
   *
   * Pre-conditions (enforced in `dispute.service.uploadEvidence` —
   * Task B):
   *   - 2FA required (evidence is part of financial resolution).
   *   - `now < dispute.evidenceWindowExpiresAt` (48h window — INV-MKT-16).
   *   - File MIME type matches the declared `type` (defense-in-depth).
   *
   * Uses `apiFetchFormData` (NOT `apiFetch`) so the browser sets the
   * multipart boundary automatically — `apiFetch` always sets
   * `Content-Type: application/json` which would break FormData uploads.
   * Same pattern as `api.market.orders.download`'s sibling uploaders.
   *
   * Authz: ownership + `requireRecent2FA`. On 403 `2FA_REQUIRED`, the
   * caller's catch block re-opens the `TwoFactorFinancialDialog`.
   *
   * Spec ref: SPEC-084 Fase 6 §5.1 (POST /:disputeId/evidence); §4.1
   *           step 3 (uploadEvidence flow + S3 signed URL); SPEC-NEGOCIO
   *           §10.9 (INV-MKT-16 evidencia 48h).
   */
  async uploadEvidence(
    disputeId: string,
    file: File,
    body: { type: EvidenceType; description?: string },
    signal?: AbortSignal,
  ): Promise<EvidenceUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', body.type);
    if (body.description) formData.append('description', body.description);
    return apiFetchFormData<EvidenceUploadResponse>(
      `/market/disputes/${encodeURIComponent(disputeId)}/evidence`,
      {
        method: 'POST',
        body: formData,
        signal,
      },
    );
  },
  /**
   * POST /api/market/disputes/:disputeId/appeal — buyer or seller
   * appeals the initial resolution (3-day window, 1 appeal per dispute).
   *
   * Pre-conditions (enforced in `dispute.service.appealDispute` — Task B):
   *   - `dispute.status === 'resolved_buyer' || 'resolved_seller'`.
   *   - `now < dispute.appealWindowExpiresAt` (3d window — INV-MKT-16).
   *   - `dispute.appeal === null` (1 appeal per dispute — INV-MKT-16).
   *   - 2FA required (operation may result in reversal — financial-grade).
   *
   * Authz: ownership + `requireRecent2FA`. On 403 `2FA_REQUIRED`, the
   * caller's catch block re-opens the `TwoFactorFinancialDialog`.
   *
   * Spec ref: SPEC-084 Fase 6 §5.1 (POST /:disputeId/appeal); §4.1 step
   *           5 (appealDispute CAS status='appealed' + set appeal sub-doc);
   *           SPEC-NEGOCIO §10.9 (INV-MKT-16 apelación 3d una sola).
   */
  async appeal(
    disputeId: string,
    body: { reason: string },
    signal?: AbortSignal,
  ): Promise<AppealResponse> {
    return apiFetch<AppealResponse>(
      `/market/disputes/${encodeURIComponent(disputeId)}/appeal`,
      {
        method: 'POST',
        body: JSON.stringify({ reason: body.reason }),
        signal,
      },
    );
  },
};
